import React, { useState } from "react";
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Badge,
  Spin,
  Empty,
  Divider,
  Space,
  Button,
  Statistic,
  Tabs,
  Alert,
} from "antd";
import {
  ShoppingOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  TagsOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FireOutlined,
  CrownOutlined,
  StarFilled,
  HeartFilled,
  PictureOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import moment from "moment";

// ✅ Import Custom Hooks
import {
  useCompletePublicMenu,
  useFormatPrice,
} from "../hooks/Usepublicmenu";
import { consts } from "../utils";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// =====================================================
// 🎨 ສີທີມ - Pink Theme
// =====================================================

const THEME = {
  primary: "#FF69B4",
  secondary: "#FFB6C1",
  accent: "#FF1493",
  light: "#FFF0F5",
  dark: "#8B008B",
  gold: "#FFD700",
  gradient: "linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)",
};

// =====================================================
// 🎨 Main Component
// =====================================================

const PublicMenuPage: React.FC = () => {
  const [selectedCurrency, setSelectedCurrency] = useState<"LAK" | "THB" | "USD">("LAK");
  const [activeTab, setActiveTab] = useState("1");

  // ✅ Use Custom Hooks
  const { data: menuResponse, isLoading, error } = useCompletePublicMenu();
  const formatPrice = useFormatPrice();

  // Helper: Get price safely
  const getPrice = (prices: any, currency: string) => {
    return prices?.[currency] || 0;
  };

  // Get Badge Color
  const getBadgeColor = (badge: string) => {
    const colors: any = {
      "HOT DEAL": "red",
      SPECIAL: "magenta",
      LIMITED: "orange",
      NEW: "pink",
      SALE: "volcano",
    };
    return colors[badge] || "pink";
  };

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: THEME.light,
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin size="large" />
          <Text style={{ fontSize: 18, color: THEME.primary }}>
            ກຳລັງໂຫລດເມນູ...
          </Text>
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: THEME.light,
          padding: 24,
        }}
      >
        <Alert
          message="ເກີດຂໍ້ຜິດພາດ"
          description="ບໍ່ສາມາດໂຫລດຂໍ້ມູນເມນູໄດ້"
          type="error"
          showIcon
        />
      </div>
    );
  }

  const menuData = menuResponse?.data;

  return (
    <Layout style={{ minHeight: "100vh", background: THEME.light }}>
      <Content style={{ padding: 0 }}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div
            style={{
              background: THEME.gradient,
              padding: "80px 24px 50px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
              }}
            />

            <div style={{ position: "relative", zIndex: 1 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <HeartFilled
                  style={{ fontSize: 60, color: "white", marginBottom: 16 }}
                />
              </motion.div>

              <Title
                level={1}
                style={{
                  color: "white",
                  margin: 0,
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: 700,
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                Momay Beauty
              </Title>
              <Text
                style={{
                  color: "rgba(255,255,255,0.95)",
                  fontSize: "clamp(1.2rem, 3vw, 1.5rem)",
                  fontWeight: 300,
                  letterSpacing: 2,
                  display: "block",
                  marginTop: 12,
                }}
              >
                ເມນູບໍລິການ & ໂປຣໂມຊັ່ນພິເສດ
              </Text>

              {/* Currency Selector */}
              <div style={{ marginTop: 32 }}>
                <Space size="middle">
                  {["LAK", "THB", "USD"].map((currency) => (
                    <Button
                      key={currency}
                      size="large"
                      type={selectedCurrency === currency ? "primary" : "default"}
                      onClick={() =>
                        setSelectedCurrency(currency as "LAK" | "THB" | "USD")
                      }
                      style={{
                        borderRadius: 25,
                        fontWeight: 600,
                        minWidth: 80,
                        height: 45,
                        ...(selectedCurrency === currency
                          ? {
                              background: "white",
                              borderColor: "white",
                              color: THEME.primary,
                              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                            }
                          : {
                              background: "rgba(255, 255, 255, 0.2)",
                              borderColor: "rgba(255, 255, 255, 0.4)",
                              color: "white",
                            }),
                      }}
                    >
                      {currency}
                    </Button>
                  ))}
                </Space>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "40px 24px" }}>
          {/* Tabs Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              centered
              size="large"
              items={[
                {
                  key: "1",
                  label: (
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      <GiftOutlined /> ໂປຣໂມຊັ່ນພິເສດ
                    </span>
                  ),
                },
                {
                  key: "2",
                  label: (
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      <TagsOutlined /> ແພັກເກັດ
                    </span>
                  ),
                },
                {
                  key: "3",
                  label: (
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      <ShoppingOutlined /> ບໍລິການ
                    </span>
                  ),
                },
              ]}
              style={{
                background: "white",
                borderRadius: 20,
                padding: "10px 20px",
                boxShadow: "0 4px 20px rgba(255, 105, 180, 0.1)",
              }}
            />
          </motion.div>

          {/* Tab Content */}
          <div style={{ marginTop: 40 }}>
            {/* Tab 1: Promotions */}
            {activeTab === "1" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {menuData?.promotions && menuData.promotions.length > 0 ? (
                  <Row gutter={[24, 24]}>
                    {menuData.promotions.map((promo, index) => (
                      <Col xs={24} sm={12} lg={8} key={promo._id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Card
                            hoverable
                            style={{
                              borderRadius: 20,
                              overflow: "hidden",
                              border: "none",
                              boxShadow: "0 8px 30px rgba(255, 105, 180, 0.15)",
                              height: "100%",
                            }}
                            bodyStyle={{ padding: 0 }}
                          >
                            {/* Image Section */}
                            <div
                              style={{
                                position: "relative",
                                height: 220,
                                background: consts.URL_PHOTO_AW3 + promo?.image
                                  ? `url(${consts.URL_PHOTO_AW3 + promo?.image})`
                                  : THEME.gradient,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {/* Overlay */}
                              {consts.URL_PHOTO_AW3 + promo?.image && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "rgba(0, 0, 0, 0.3)",
                                  }}
                                />
                              )}

                              {/* Badge Ribbon */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: 15,
                                  right: -35,
                                  background: THEME.accent,
                                  color: "white",
                                  padding: "8px 40px",
                                  transform: "rotate(45deg)",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                                  zIndex: 2,
                                }}
                              >
                                {promo.badge}
                              </div>

                              {/* Discount Badge */}
                              <div
                                style={{
                                  background: "white",
                                  width: 140,
                                  height: 140,
                                  borderRadius: "50%",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                                  position: "relative",
                                  zIndex: 1,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 48,
                                    fontWeight: 900,
                                    color: THEME.accent,
                                    lineHeight: 1,
                                  }}
                                >
                                  {promo.discountPercentage}%
                                </div>
                                <Text
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: THEME.primary,
                                    marginTop: 4,
                                  }}
                                >
                                  OFF
                                </Text>
                              </div>

                              {/* Fire Icon */}
                              <FireOutlined
                                style={{
                                  position: "absolute",
                                  top: 20,
                                  left: 20,
                                  fontSize: 32,
                                  color: "white",
                                  zIndex: 2,
                                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                }}
                              />
                            </div>

                            {/* Content Section */}
                            <div style={{ padding: 24 }}>
                              <Title
                                level={4}
                                style={{
                                  margin: "0 0 12px 0",
                                  color: THEME.dark,
                                }}
                              >
                                {promo.title}
                              </Title>
                              <Paragraph
                                type="secondary"
                                style={{ marginBottom: 16, minHeight: 44 }}
                                ellipsis={{ rows: 2 }}
                              >
                                {promo.description}
                              </Paragraph>

                              <Divider style={{ margin: "16px 0" }} />

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Space>
                                  <ClockCircleOutlined
                                    style={{ color: THEME.primary }}
                                  />
                                  <Text style={{ fontSize: 13, color: "#666" }}>
                                    {moment(promo.startDate).format("DD/MM/YY")} ຫາ{" "}
                                    {moment(promo.endDate).format("DD/MM/YY")}
                                  </Text>
                                </Space>
                                <Tag
                                  color={getBadgeColor(promo.badge)}
                                  style={{
                                    borderRadius: 12,
                                    padding: "4px 12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {promo.badge}
                                </Tag>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="ບໍ່ມີໂປຣໂມຊັ່ນໃນຂະນະນີ້"
                  />
                )}
              </motion.div>
            )}

            {/* Tab 2: Packages */}
            {activeTab === "2" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {menuData?.packages && menuData.packages.length > 0 ? (
                  <Row gutter={[24, 24]}>
                    {menuData.packages.map((pkg, index) => (
                      <Col xs={24} sm={12} lg={8} key={pkg._id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Card
                            hoverable
                            style={{
                              borderRadius: 20,
                              border: `2px solid ${THEME.secondary}`,
                              boxShadow: "0 8px 30px rgba(255, 105, 180, 0.1)",
                              height: "100%",
                              transition: "all 0.3s ease",
                              overflow: "hidden",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-8px)";
                              e.currentTarget.style.boxShadow =
                                "0 12px 40px rgba(255, 105, 180, 0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow =
                                "0 8px 30px rgba(255, 105, 180, 0.1)";
                            }}
                            bodyStyle={{ padding: 0 }}
                          >
                            {/* Package Image */}
                            {consts.URL_PHOTO_AW3 + pkg.image ? (
                              <div
                                style={{
                                  width: "100%",
                                  height: 200,
                                  background: `url(${consts.URL_PHOTO_AW3 + pkg.image})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  position: "relative",
                                }}
                              >
                                {/* Overlay */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background:
                                      "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)",
                                  }}
                                />
                                {/* Sessions Badge */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 16,
                                    right: 16,
                                    background: THEME.gradient,
                                    color: "white",
                                    borderRadius: 20,
                                    padding: "8px 20px",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    boxShadow: "0 4px 15px rgba(255, 105, 180, 0.5)",
                                  }}
                                >
                                  {pkg.sessions} ຄັ້ງ
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: 200,
                                  background: THEME.light,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <PictureOutlined
                                  style={{ fontSize: 48, color: THEME.secondary }}
                                />
                              </div>
                            )}

                            {/* Card Content */}
                            <div style={{ padding: 24 }}>
                              {/* Header */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: 16,
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <Tag
                                    color={THEME.primary}
                                    style={{
                                      borderRadius: 12,
                                      padding: "4px 12px",
                                      marginBottom: 8,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {pkg.categoryName}
                                  </Tag>
                                  <Title level={4} style={{ margin: 0 }}>
                                    {pkg.courseName}
                                  </Title>
                                </div>
                                {!pkg.image && (
                                  <Badge
                                    count={
                                      <div
                                        style={{
                                          background: THEME.gradient,
                                          color: "white",
                                          borderRadius: 20,
                                          padding: "6px 16px",
                                          fontSize: 14,
                                          fontWeight: 700,
                                          boxShadow:
                                            "0 4px 15px rgba(255, 105, 180, 0.3)",
                                        }}
                                      >
                                        {pkg.sessions} ຄັ້ງ
                                      </div>
                                    }
                                  />
                                )}
                              </div>

                              {/* Description */}
                              {pkg.description && (
                                <Paragraph
                                  type="secondary"
                                  ellipsis={{ rows: 2 }}
                                  style={{ marginBottom: 20, minHeight: 44 }}
                                >
                                  {pkg.description}
                                </Paragraph>
                              )}

                              <Divider style={{ margin: "20px 0" }} />

                              {/* Price */}
                              <div
                                style={{
                                  background: THEME.light,
                                  borderRadius: 15,
                                  padding: 20,
                                  textAlign: "center",
                                }}
                              >
                                <Statistic
                                  value={getPrice(pkg.prices, selectedCurrency)}
                                  suffix={
                                    selectedCurrency === "LAK"
                                      ? "₭"
                                      : selectedCurrency === "THB"
                                      ? "฿"
                                      : "$"
                                  }
                                  valueStyle={{
                                    color: THEME.accent,
                                    fontSize: 32,
                                    fontWeight: 900,
                                  }}
                                />
                                <Space
                                  style={{ marginTop: 12 }}
                                  split={<Divider type="vertical" />}
                                >
                                  <Text type="secondary" style={{ fontSize: 13 }}>
                                    <ClockCircleOutlined /> {pkg.sessions} ຄັ້ງ
                                  </Text>
                                  <Text
                                    style={{
                                      color: THEME.primary,
                                      fontWeight: 600,
                                      fontSize: 13,
                                    }}
                                  >
                                    <CrownOutlined /> ແນະນຳ
                                  </Text>
                                </Space>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="ບໍ່ມີແພັກເກັດໃນຂະນະນີ້"
                  />
                )}
              </motion.div>
            )}

            {/* Tab 3: Services */}
            {activeTab === "3" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {menuData?.menu && menuData.menu.length > 0 ? (
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    {menuData.menu.map((category, catIndex) => (
                      <motion.div
                        key={category._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: catIndex * 0.1 }}
                      >
                        <Card
                          style={{
                            borderRadius: 20,
                            border: "none",
                            boxShadow: "0 8px 30px rgba(255, 105, 180, 0.1)",
                          }}
                        >
                          {/* Category Header */}
                          <div
                            style={{
                              background: THEME.light,
                              padding: "20px 24px",
                              borderRadius: 15,
                              marginBottom: 24,
                            }}
                          >
                            <Space size="middle" align="center">
                              <div
                                style={{
                                  fontSize: 40,
                                  width: 60,
                                  height: 60,
                                  background: "white",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 4px 15px rgba(255, 105, 180, 0.15)",
                                }}
                              >
                                {category.icon}
                              </div>
                              <div>
                                <Title
                                  level={3}
                                  style={{ margin: 0, color: THEME.dark }}
                                >
                                  {category.name}
                                </Title>
                                {category.description && (
                                  <Text type="secondary">{category.description}</Text>
                                )}
                              </div>
                            </Space>
                          </div>

                          {/* Services Grid */}
                          <Row gutter={[16, 16]}>
                            {category.services.map((service, serviceIndex) => (
                              <Col xs={24} lg={12} key={service._id}>
                                <motion.div
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{
                                    duration: 0.4,
                                    delay: serviceIndex * 0.05,
                                  }}
                                >
                                  <Card
                                    size="small"
                                    hoverable
                                    style={{
                                      borderRadius: 15,
                                      border: `1px solid ${THEME.secondary}`,
                                      background: "white",
                                      overflow: "hidden",
                                    }}
                                    bodyStyle={{ padding: 0 }}
                                  >
                                    {/* Service Image */}
                                    {service.image && (
                                      <div
                                        style={{
                                          width: "100%",
                                          height: 160,
                                          background: `url(${consts.URL_PHOTO_AW3 + service.image})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                          position: "relative",
                                        }}
                                      >
                                        {/* Star Badge */}
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: 12,
                                            left: 12,
                                            background: "white",
                                            borderRadius: "50%",
                                            width: 32,
                                            height: 32,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                          }}
                                        >
                                          <StarFilled
                                            style={{
                                              color: THEME.gold,
                                              fontSize: 16,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Service Info */}
                                    <div style={{ padding: 16 }}>
                                      <Row gutter={16} align="middle">
                                        <Col flex="auto">
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              marginBottom: 8,
                                            }}
                                          >
                                            {!service.image && (
                                              <StarFilled
                                                style={{
                                                  color: THEME.gold,
                                                  fontSize: 16,
                                                }}
                                              />
                                            )}
                                            <Title level={5} style={{ margin: 0 }}>
                                              {service.name}
                                            </Title>
                                          </div>
                                          {service.description && (
                                            <Paragraph
                                              type="secondary"
                                              style={{
                                                margin: "8px 0",
                                                fontSize: 13,
                                              }}
                                              ellipsis={{ rows: 2 }}
                                            >
                                              {service.description}
                                            </Paragraph>
                                          )}
                                        </Col>

                                        {/* Price */}
                                        <Col>
                                          <div
                                            style={{
                                              textAlign: "center",
                                              padding: "12px 20px",
                                              background: THEME.light,
                                              borderRadius: 12,
                                            }}
                                          >
                                            <Statistic
                                              value={getPrice(
                                                service.prices,
                                                selectedCurrency
                                              )}
                                              suffix={
                                                selectedCurrency === "LAK"
                                                  ? "₭"
                                                  : selectedCurrency === "THB"
                                                  ? "฿"
                                                  : "$"
                                              }
                                              valueStyle={{
                                                color: THEME.accent,
                                                fontSize: 22,
                                                fontWeight: 700,
                                              }}
                                            />
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: 11 }}
                                            >
                                              ຕໍ່ຄັ້ງ
                                            </Text>
                                          </div>
                                        </Col>
                                      </Row>

                                      {/* Service Packages */}
                                      {service.packages &&
                                        service.packages.length > 0 && (
                                          <div
                                            style={{
                                              marginTop: 16,
                                              paddingTop: 16,
                                              borderTop: `1px dashed ${THEME.secondary}`,
                                            }}
                                          >
                                            <Text
                                              strong
                                              style={{
                                                fontSize: 12,
                                                color: THEME.primary,
                                              }}
                                            >
                                              💎 ແພັກເກັດພິເສດ:
                                            </Text>
                                            <Row gutter={8} style={{ marginTop: 12 }}>
                                              {service.packages.map((pkg, idx) => (
                                                <Col xs={12} sm={8} key={idx}>
                                                  <div
                                                    style={{
                                                      background: THEME.light,
                                                      padding: "10px 12px",
                                                      borderRadius: 10,
                                                      border: `1px solid ${THEME.secondary}`,
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    <Badge
                                                      count={`${pkg.sessions}x`}
                                                      style={{
                                                        background: THEME.primary,
                                                        fontSize: 11,
                                                      }}
                                                    />
                                                    <div
                                                      style={{
                                                        marginTop: 6,
                                                        fontSize: 16,
                                                        fontWeight: 700,
                                                        color: THEME.accent,
                                                      }}
                                                    >
                                                      {formatPrice(
                                                        getPrice(
                                                          pkg?.price,
                                                          selectedCurrency
                                                        ),
                                                        selectedCurrency
                                                      )}
                                                    </div>
                                                    <Text
                                                      type="secondary"
                                                      style={{ fontSize: 10 }}
                                                    >
                                                      {pkg.sessions} ຄັ້ງ
                                                    </Text>
                                                  </div>
                                                </Col>
                                              ))}
                                            </Row>
                                          </div>
                                        )}
                                    </div>
                                  </Card>
                                </motion.div>
                              </Col>
                            ))}
                          </Row>
                        </Card>
                      </motion.div>
                    ))}
                  </Space>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="ບໍ່ມີບໍລິການໃນຂະນະນີ້"
                  />
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div
            style={{
              background: THEME.gradient,
              color: "white",
              padding: "50px 24px",
              marginTop: 80,
            }}
          >
            <div
              style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center" }}
            >
              <HeartFilled style={{ fontSize: 48, marginBottom: 24 }} />

              <Row gutter={[48, 24]} justify="center">
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size="small">
                    <PhoneOutlined style={{ fontSize: 28 }} />
                    <Text
                      style={{ color: "white", fontSize: 18, fontWeight: 600 }}
                    >
                      📞 020 1234 5678
                    </Text>
                    <Text
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 14,
                      }}
                    >
                      ຕິດຕໍ່ເຮົາໄດ້ທຸກເວລາ
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size="small">
                    <EnvironmentOutlined style={{ fontSize: 28 }} />
                    <Text
                      style={{ color: "white", fontSize: 18, fontWeight: 600 }}
                    >
                      📍 ວຽງຈັນ, ສປປ ລາວ
                    </Text>
                    <Text
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 14,
                      }}
                    >
                      ຕັ້ງຢູ່ໃຈກາງເມືອງ
                    </Text>
                  </Space>
                </Col>
              </Row>

              <Divider
                style={{
                  borderColor: "rgba(255,255,255,0.3)",
                  margin: "40px 0 30px",
                }}
              />

              <div>
                <Text
                  style={{
                    color: "white",
                    fontSize: 16,
                    display: "block",
                    marginBottom: 16,
                  }}
                >
                  ✨ ຂໍຂອບໃຈລູກຄ້າທີ່ໃຊ້ບໍລິການ ✨
                </Text>
                <div style={{ fontSize: 36 }}>💖🌸💖</div>
              </div>
            </div>
          </div>
        </motion.div>
      </Content>
    </Layout>
  );
};

export default PublicMenuPage;